import { Fragment, useEffect, useState } from 'react';
import {
  useCreateLead, useDeleteLead, useLeads, useUpdateLead, useUsers,
} from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useDebounced } from '@/hooks/useDebounced';
import { useToast } from '@/hooks/useToast';
import { LeadDetail } from '@/components/LeadDetail';
import { NewLeadModal } from '@/components/NewLeadModal';
import { ProductCell } from '@/components/leads/ProductCell';
import {
  Avatar, ConfirmDialog, EmptyState, ErrorState, Pagination, TableSkeleton,
} from '@/components/ui';
import { IconChevronDown, IconChevronRight, IconPlus, IconSearch } from '@/components/Icons';
import { formatBRLShort, formatDate, MESES, STATUS_LABELS } from '@/lib/format';
import type { Lead, LeadStatus } from '@/lib/types';
import { CURRENT_YEAR, YEAR_OPTIONS } from '@/lib/years';

const STATUS_TONE: Record<LeadStatus, string> = {
  ACTIVE: 'chip',
  WON: 'chip chip-positive',
  LOST: 'chip chip-negative',
};

/** Valor do filtro de responsável que pede os clientes sem responsável (o mesmo da API). */
const NO_OWNER = 'none';
/** Colunas da tabela — a linha vazia e o detalhe ocupam todas. */
const COLUMNS = 9;

/** Nome e, embaixo, o e-mail (ou o telefone) — é o que identifica um comprador da Ephra. */
function NameCell({ lead }: { lead: Lead }) {
  const contact = lead.email ?? lead.phone;
  return (
    <>
      <div style={{ fontWeight: 500 }}>{lead.name}</div>
      {contact ? <div className="muted truncate" style={{ fontSize: 11.5, maxWidth: 200 }} title={contact}>{contact}</div> : null}
    </>
  );
}

export function LeadsPage() {
  const { isAdmin } = useAuth();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [source, setSource] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string; purchases: number } | null>(null);

  const debouncedSearch = useDebounced(search);

  const { data: members } = useUsers({}, { enabled: isAdmin });
  const { data, isLoading, error } = useLeads({
    search: debouncedSearch || undefined,
    ownerId: ownerId || undefined,
    source: source || undefined,
    month: month || undefined,
    year: year || undefined,
    status: status || undefined,
    page,
    pageSize: 20,
  });

  const updateLead = useUpdateLead();
  const createLead = useCreateLead();
  const deleteLead = useDeleteLead();

  // Qualquer mudança de filtro volta para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ownerId, source, month, year, status]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await deleteLead.mutateAsync(pendingDelete.id);
      toast.success(`Cliente "${pendingDelete.name}" excluído.`);
      if (expandedId === pendingDelete.id) setExpandedId(null);
      setPendingDelete(null);
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  if (error) return <ErrorState message={(error as Error).message} />;

  return (
    <>
      <h1 className="page-title">Clientes</h1>
      <p className="page-lede">
        {isAdmin
          ? 'Clientes atendidos por toda a operação — os cadastrados à mão, quem comprou na Ephra e os leads atendidos na For All Sales.'
          : 'Os clientes que você atende — inclusive os leads da For All Sales — e o que compraram na Ephra.'}
      </p>


      <div className="toolbar">
        {/* A busca ocupa a linha de cima; os filtros e o botão, a de baixo. */}
        <div className="search-input toolbar-search" style={{ flexBasis: '100%' }}>
          <span className="search-input-icon" aria-hidden="true"><IconSearch /></span>
          <input
            className="inp"
            placeholder="Buscar por nome, e-mail, telefone ou produto"
            aria-label="Buscar clientes"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {isAdmin ? (
          <select className="inp" value={ownerId} onChange={(event) => setOwnerId(event.target.value)} aria-label="Responsável">
            <option value="">Todos os responsáveis</option>
            <option value={NO_OWNER}>Sem responsável</option>
            {members?.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
          </select>
        ) : null}

        <select className="inp" value={source} onChange={(event) => setSource(event.target.value)} aria-label="Origem">
          <option value="">Todas as origens</option>
          <option value="EPHRA">Compraram na Ephra</option>
          <option value="FORALLSALES">Atendidos na For All Sales</option>
          <option value="MANUAL">Cadastrados à mão</option>
        </select>

        <select className="inp" value={month} onChange={(event) => setMonth(event.target.value)} aria-label="Mês">
          <option value="">Todos os meses</option>
          {MESES.map((label, index) => <option key={label} value={index + 1}>{label}</option>)}
        </select>

        <select className="inp" value={year} onChange={(event) => setYear(event.target.value)} aria-label="Ano">
          {YEAR_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>

        <select className="inp" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Status">
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>

        <div className="spacer" />
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          <IconPlus /> Novo cliente
        </button>
      </div>

      {isLoading && !data ? (
        <TableSkeleton rows={6} columns={8} />
      ) : (
        <>
          <div className="table-wrap">
            <table className="tbl tbl-dense tbl-cards">
              <thead>
                <tr>
                  <th style={{ width: 28 }} />
                  <th>Nome</th>
                  <th>Responsável</th>
                  <th className="nowrap">Data</th>
                  <th>Produtos</th>
                  <th className="right" title="Comprovantes e compras na Ephra">Vendas</th>
                  <th className="right">Vendido</th>
                  <th className="right">Lucro</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data && data.items.length === 0 ? (
                  <tr><td colSpan={COLUMNS}><EmptyState message="Nenhum cliente encontrado com esses filtros." /></td></tr>
                ) : null}

                {data?.items.map((lead) => {
                  const open = expandedId === lead.id;
                  return (
                    <Fragment key={lead.id}>
                      <tr
                        className="row-hover row-click"
                        onClick={() => setExpandedId(open ? null : lead.id)}
                      >
                        <td className="muted cell-hide-sm">{open ? <IconChevronDown /> : <IconChevronRight />}</td>
                        <td className="cell-main"><NameCell lead={lead} /></td>
                        <td data-label="Responsável">
                          {lead.ownerId ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                              <Avatar name={lead.ownerName ?? '?'} color={lead.ownerColor} photo={lead.ownerPhotoUrl} size={22} />
                              <span className="nowrap">{lead.ownerName ?? '—'}</span>
                            </span>
                          ) : (
                            <span className="muted nowrap">Sem responsável</span>
                          )}
                        </td>
                        <td className="num nowrap" data-label="Data">
                          <div>{formatDate(lead.date)}</div>
                          <div className="muted" style={{ fontSize: 11.5 }}>{lead.time}</div>
                        </td>
                        {/* No celular os produtos ficam no detalhe, junto das compras. */}
                        <td className="cell-hide-sm"><ProductCell lead={lead} /></td>
                        <td className="num right cell-third" data-label="Vendas">{lead.saleCount}</td>
                        <td className="num right emphasis cell-key" style={{ fontWeight: 500 }} data-label="Vendido">{formatBRLShort(lead.soldTotal)}</td>
                        <td className="num right positive cell-third" data-label="Lucro">{formatBRLShort(lead.profit)}</td>
                        <td className="cell-third" data-label="Status"><span className={STATUS_TONE[lead.status]}>{STATUS_LABELS[lead.status]}</span></td>
                      </tr>
                      {open ? (
                        <tr>
                          <td colSpan={COLUMNS} style={{ padding: 0 }}>
                            <LeadDetail
                              lead={lead}
                              onDelete={() => setPendingDelete({ id: lead.id, name: lead.name, purchases: lead.purchases.length })}
                              onSaveField={async (patch) => {
                                try {
                                  await updateLead.mutateAsync({ id: lead.id, ...patch });
                                } catch (caught) {
                                  toast.fromError(caught);
                                }
                              }}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data ? (
            <Pagination page={data.page} totalPages={data.totalPages} total={data.total} onChange={setPage} />
          ) : null}
        </>
      )}

      {creating ? (
        <NewLeadModal
          onClose={() => setCreating(false)}
          onSubmit={async (payload) => {
            try {
              const { lead } = await createLead.mutateAsync(payload);
              toast.success(`Cliente "${lead.name}" cadastrado.`);
              setCreating(false);
              setExpandedId(lead.id);
            } catch (caught) {
              toast.fromError(caught);
            }
          }}
          busy={createLead.isPending}
        />
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          title="Excluir cliente"
          danger
          confirmLabel="Excluir"
          busy={deleteLead.isPending}
          message={
            <>
              Excluir <strong>{pendingDelete.name}</strong>? Os comprovantes vinculados a este cliente
              continuam valendo — só deixam de apontar para ele.
              {pendingDelete.purchases > 0
                ? ' As compras da Ephra saem daqui junto (na Ephra nada muda) e voltam se a importação das vendas rodar de novo.'
                : ''}{' '}
              Esta ação não pode ser desfeita.
            </>
          }
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </>
  );
}
